'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { KeyboardEvent } from 'react';
import { Button } from '../../../components/button';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import { TraceLine } from '../components/trace';
import { AgentStatusDots } from './AgentStatusDots';
import { headlineKindForParentTool } from '../components/chat-headline-actions';
import {
  ChatHeadlineStatusIcon,
  type ChatHeadlineStatusState,
} from '../components/chat-headline-loading';
import type {
  SubagentActivityCount,
  SubagentActivityItem,
} from './subagent-activity';
import type { SubagentTaskDetails } from './subagent-task';
import {
  isParallelAgentAlive,
  type ParallelAgentStatus,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';
import { useParallelAgentRowCopy } from './useParallelAgentRowCopy';

function headlineStatusForAgent(
  status: ParallelAgentStatus,
  isAlive: boolean,
): ChatHeadlineStatusState | undefined {
  if (isAlive) {
    return 'running';
  }

  if (status === 'completed') {
    return 'completed';
  }

  if (status === 'aborted') {
    return 'stopped';
  }

  if (status === 'error') {
    return 'failed';
  }

  return undefined;
}

export function ParallelAgentRow({
  execution,
  messages,
  isSelected,
  stopLabel,
  task,
  indexedActivity,
  onOpen,
  onStop,
}: {
  execution: ParallelAgentExecution;
  messages: TerraUIMessage[];
  isSelected: boolean;
  stopLabel: string;
  task?: SubagentTaskDetails;
  indexedActivity?: {
    items: SubagentActivityItem[];
    counts: SubagentActivityCount[];
  };
  onOpen: () => void;
  onStop?: () => void;
}) {
  const { name, title, status, statusLabel, activity } =
    useParallelAgentRowCopy({
      execution,
      messages,
      task,
      activity: indexedActivity,
    });
  const isAlive = isParallelAgentAlive(status);
  const canStop = !!onStop && isAlive;
  const headlineKind = headlineKindForParentTool(execution.agentType);
  const headlineStatus = headlineStatusForAgent(status, isAlive);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <HStack
      className="group"
      role="button"
      tabIndex={0}
      aria-current={isSelected || undefined}
      data-testid="subagent-overlay-row"
      data-status={status}
      gap={2.5}
      alignItems="flex-start"
      width="full"
      minW={0}
      px={2}
      py={1.5}
      borderRadius="md"
      cursor="pointer"
      bg={isSelected ? 'bg.muted' : 'transparent'}
      _hover={{ bg: isSelected ? 'bg.muted' : 'bg.subtle' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'border.outline' }}
      transition="background-color 0.15s ease"
      onClick={onOpen}
      onKeyDown={handleKeyDown}
    >
      <VStack alignItems="stretch" gap={0.5} flex="1" minW={0}>
        <TraceLine
          title={name}
          status={isAlive ? 'running' : undefined}
          icon={
            <Box
              flexShrink={0}
              role={headlineStatus ? 'img' : undefined}
              aria-label={headlineStatus ? statusLabel : undefined}
            >
              {headlineStatus ? (
                <ChatHeadlineStatusIcon
                  kind={headlineKind}
                  state={headlineStatus}
                  spinnerSource="kind"
                />
              ) : (
                <AgentStatusDots status={status} label={statusLabel} />
              )}
            </Box>
          }
          meta={
            title ? (
              <MetaDefault
                color="fg.muted"
                truncate
                minW={0}
                data-testid="subagent-task-title"
              >
                {title}
              </MetaDefault>
            ) : null
          }
          trailing={
            canStop ? (
              <Box
                flexShrink={0}
                opacity={isSelected ? 1 : 0}
                _groupHover={{ opacity: 1 }}
                _groupFocusWithin={{ opacity: 1 }}
                transition="opacity 0.15s ease"
              >
                <Button
                  variant="ghost"
                  size="xs"
                  data-testid="abort-subagent"
                  onClick={(event) => {
                    event.stopPropagation();
                    onStop?.();
                  }}
                >
                  {stopLabel}
                </Button>
              </Box>
            ) : null
          }
        />
        <HStack
          gap={1.5}
          minW={0}
          alignItems="baseline"
          data-testid="subagent-activity-line"
          animation={
            activity.isLive
              ? 'shimmer-opacity 2s ease-in-out infinite'
              : undefined
          }
          _motionReduce={{ animation: 'none' }}
        >
          <MetaDefault
            color="fg.muted"
            truncate
            flexShrink={activity.target ? 0 : 1}
            minW={0}
          >
            {activity.label}
          </MetaDefault>
          {activity.target ? (
            <MonoSmall color="fg.subtle" truncate minW={0}>
              {activity.target}
            </MonoSmall>
          ) : null}
        </HStack>
      </VStack>
    </HStack>
  );
}
